import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FileText, Printer, ArrowLeft, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaiverReceiptPage() {
  const { licenseId } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/redemption/waiver-receipt/${licenseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [licenseId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-gray-700">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading receipt…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900">Receipt unavailable</h1>
          <p className="text-gray-600 mt-2">{error}</p>
          <Link to="/dashboard" className="inline-flex items-center gap-1 mt-4 px-4 py-2 bg-gray-900 text-white rounded">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1 px-4 py-2 bg-gray-900 text-white rounded text-sm">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>

        <div className="bg-white rounded-lg shadow print:shadow-none p-8 md:p-12 text-gray-900">
          <header className="border-b border-gray-200 pb-4 mb-6">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-widest">
              <FileText className="w-4 h-4" /> Refund waiver receipt
            </div>
            <h1 className="text-2xl font-bold mt-1">AEO Orchestra — Founders Lifetime License</h1>
            <p className="text-sm text-gray-600 mt-1">Solaris Code SL · aeo-orchestra.com</p>
          </header>

          <section className="space-y-4 text-sm leading-relaxed">
            <p>This document confirms that the holder of the license below has voluntarily waived the platform-mandated refund window in exchange for immediate access to the full balance of redemption credits attached to this license.</p>

            <table className="w-full text-sm">
              <tbody>
                <Row label="License key" value={<code>{data.license_key}</code>} />
                <Row label="Account email" value={data.user_email} />
                <Row label="Package" value={data.package_name} />
                <Row label="Source platform(s)" value={(data.platform_set || []).join(', ') || '—'} />
                <Row label="Number of waiver events" value={data.waivers.length} />
              </tbody>
            </table>

            <h2 className="font-semibold text-gray-800 mt-6">Waiver events</h2>
            <table className="w-full text-xs border-t border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Platform</th>
                  <th className="text-left p-2">Waived at (UTC)</th>
                  <th className="text-left p-2">Confirmation ID</th>
                </tr>
              </thead>
              <tbody>
                {data.waivers.map(w => (
                  <tr key={w.code} className="border-t border-gray-100">
                    <td className="p-2 font-mono">{w.code}</td>
                    <td className="p-2">{w.platform || '—'}</td>
                    <td className="p-2">{w.waived_at}</td>
                    <td className="p-2 font-mono text-[10px]">{w.confirmation_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2 className="font-semibold text-gray-800 mt-6">Legal effect</h2>
            <p>By waiving the refund window through the AEO Orchestra dashboard with the explicit confirmation phrase, the user has irrevocably forfeited the right to obtain a refund through the LTD distribution platform(s) listed above for this purchase. The lifetime nature of the license is unaffected by this waiver. The license remains the property of the user under its original terms.</p>

            <p className="text-xs text-gray-500 mt-6 border-t border-gray-200 pt-4">
              Issued automatically by the AEO Orchestra system upon waiver event(s) above. For questions: support@aeo-orchestra.com.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <tr className="border-t border-gray-100">
      <td className="p-2 text-gray-500 align-top w-1/3">{label}</td>
      <td className="p-2 font-medium break-all">{value}</td>
    </tr>
  );
}
